import React, { lazy, Suspense, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate, Link } from 'react-router-dom';

// Eager load critical pages (login, register, landing)
import { LoginForm } from '../components/auth/LoginForm';
import { LandingPage } from '../pages/LandingPage';
import RegisterPage from '../pages/RegisterPage';
import PricingPage from '../pages/PricingPage';
import { ForgotPassword } from '../pages/ForgotPassword';

// Lazy load non-critical pages for code splitting
const Dashboard = lazy(() => import('../pages/Dashboard').then(m => ({ default: m.Dashboard })));
const AdminPage = lazy(() => import('../pages/AdminPage').then(m => ({ default: m.AdminPage })));
const LedgerDemo = lazy(() => import('../pages/LedgerDemo').then(m => ({ default: m.LedgerDemo })));
const PropertyDetail = lazy(() => import('../pages/PropertyDetail').then(m => ({ default: m.PropertyDetail })));
const Ranking = lazy(() => import('../pages/Ranking').then(m => ({ default: m.Ranking })));
const PortfolioAnalysis = lazy(() => import('../pages/PortfolioAnalysis').then(m => ({ default: m.PortfolioAnalysis })));
const MarketData = lazy(() => import('../pages/MarketData').then(m => ({ default: m.MarketData })));
const ScenarioPlanner = lazy(() => import('../pages/ScenarioPlanner').then(m => ({ default: m.ScenarioPlanner })));
const InvestmentTools = lazy(() => import('../pages/InvestmentTools').then(m => ({ default: m.InvestmentTools })));

const BlogListPage = lazy(() => import('../pages/BlogListPage').then(m => ({ default: m.BlogListPage })));
const BlogPostPage = lazy(() => import('../pages/BlogPostPage').then(m => ({ default: m.BlogPostPage })));
const StockPortfolioPage = lazy(() => import('../pages/StockPortfolioPage').then(m => ({ default: m.StockPortfolioPage })));
const ContentPageView = lazy(() => import('../pages/ContentPageView').then(m => ({ default: m.ContentPageView })));
const ExpiredTrialPage = lazy(() => import('../pages/ExpiredTrialPage'));
const SettingsPage = lazy(() => import('../pages/SettingsPage').then(m => ({ default: m.SettingsPage })));
const YieldCalculatorPage = lazy(() => import('../pages/YieldCalculatorPage').then(m => ({ default: m.YieldCalculatorPage })));
const PropertyCalculatorHub = lazy(() => import('../pages/calculators/PropertyCalculatorHub').then(m => ({ default: m.PropertyCalculatorHub })));
const UpfrontCostsCalculator = lazy(() => import('../pages/calculators/UpfrontCostsCalculator').then(m => ({ default: m.UpfrontCostsCalculator })));
const BorrowingPowerCalculator = lazy(() => import('../pages/calculators/BorrowingPowerCalculator').then(m => ({ default: m.BorrowingPowerCalculator })));
const RateSensitivityCalculator = lazy(() => import('../pages/calculators/RateSensitivityCalculator').then(m => ({ default: m.RateSensitivityCalculator })));
const ExtraRepaymentsCalculator = lazy(() => import('../pages/calculators/ExtraRepaymentsCalculator').then(m => ({ default: m.ExtraRepaymentsCalculator })));
const FHOGCalculator = lazy(() => import('../pages/calculators/FHOGCalculator').then(m => ({ default: m.FHOGCalculator })));
const GrowthEquityCalculator = lazy(() => import('../pages/calculators/GrowthEquityCalculator').then(m => ({ default: m.GrowthEquityCalculator })));
const InvestmentPlannerPage = lazy(() => import('../pages/InvestmentPlannerPage').then(m => ({ default: m.InvestmentPlannerPage })));
const SOAReportPage = lazy(() => import('../pages/SOAReportPage').then(m => ({ default: m.SOAReportPage })));
const AboutUs = lazy(() => import('../pages/AboutUs').then(m => ({ default: m.AboutUs })));
const ContactUs = lazy(() => import('../pages/ContactUs').then(m => ({ default: m.ContactUs })));
const ProductTechnicalOverview = lazy(() => import('../pages/ProductTechnicalOverview').then(m => ({ default: m.ProductTechnicalOverview })));
const PropertyInspectionPage = lazy(() => import('../pages/PropertyInspectionPage').then(m => ({ default: m.PropertyInspectionPage })));
const RetirementProjectionPage = lazy(() => import('../pages/RetirementProjectionPage'));
const DebugManagementFees = lazy(() => import('../pages/DebugManagementFees').then(m => ({ default: m.DebugManagementFees })));

// Import components
import { Layout } from '../components/layout/Layout';
import { AuthProvider, useAuth } from '../services/AuthContext';
import { PortfolioProvider } from '../services/PortfolioContext';
import { AlertsProvider } from '../contexts/AlertsContext';
import { ProtectedRoute } from '../components/auth/ProtectedRoute';
import { CountryProvider } from '../services/CountryContext';

import { ToastProvider } from '../components/ui/Toast';
import { Footer } from '../components/layout/Footer';
import { ThemeManager } from '../components/layout/ThemeManager';
import { ThemeProvider } from '../contexts/ThemeContext';
import { ScrollToTop } from '../components/layout/ScrollToTop';
import { analytics } from '../services/analytics';

// Loading fallback component for lazy-loaded pages
function PageLoader() {
  return (
    <div className="flex items-center justify-center min-h-screen">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-primary mx-auto mb-4"></div>
        <p className="text-gray-600">Loading...</p>
      </div>
    </div>
  );
}

// Helper component to handle blog routes with appropriate layout
function BlogRoute({ isPost = false }: { isPost?: boolean }) {
  const { user, loading } = useAuth();

  if (loading) return <PageLoader />;

  if (user) {
    // If logged in, we want AuthenticatedApp to handle it with its Layout
    return <AuthenticatedApp />;
  }

  return (
    <PublicPageWrapper>
      {isPost ? <BlogPostPage /> : <BlogListPage />}
    </PublicPageWrapper>
  );
}

// Public page wrapper with footer for unauthenticated users
function PublicPageWrapper({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen min-h-[100dvh] bg-white flex flex-col">
      {/* Simple header for public pages */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm border-b border-gray-200">
        <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-3">
              <img src="/logo.jpg" alt="CanvasEstate" className="h-10 w-auto rounded-lg" loading="lazy" />
              <span className="text-xl font-bold text-brand-secondary hidden xs:inline">CanvasEstate</span>
            </Link>
            <div className="flex items-center gap-2 sm:gap-4">
              <Link
                to="/calculators"
                className="text-sm font-medium text-gray-700 hover:text-brand-primary transition-colors hidden md:block"
              >
                Calculators
              </Link>
              <Link
                to="/pricing"
                className="text-sm font-medium text-gray-700 hover:text-brand-primary transition-colors hidden md:block"
              >
                Pricing
              </Link>
              <Link
                to="/login"
                className="text-sm font-medium text-gray-700 hover:text-brand-primary transition-colors hidden sm:block"
              >
                Sign In
              </Link>
              <Link
                to="/register"
                className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-brand-primary hover:bg-blue-700 rounded-lg transition-colors shadow-sm"
              >
                Get Started Free
              </Link>
            </div>
          </div>
        </nav>
      </header>

      {/* Main content */}
      <main className="flex-1 pt-20 pb-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {children}
        </div>
      </main>

      <Footer />
    </div>
  );
}

// Component to show landing page for unauthenticated users or redirect to dashboard
function PublicHome() {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-brand-primary"></div>
      </div>
    );
  }

  if (user) {
    return <Navigate to="/dashboard" replace />;
  }

  return <LandingPage />;
}

// Component to handle authentication checks - exported for testing
export function AuthenticatedApp() {
  const { user, loading, isAdmin, isAuthorized } = useAuth();
  const location = useLocation();

  // Show loading state while auth is initializing
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-primary"></div>
      </div>
    );
  }

  // Check authentication
  if (!user) {
    return <LoginForm />;
  }

  // Redirect to registration if user is authenticated but not authorized (not in database)
  // Skip redirect if we are already on the register page to avoid loops
  if (!isAuthorized && location.pathname !== '/register') {
    return <Navigate to="/register" replace />;
  }

  // Render authenticated and authorized content
  return (
    <PortfolioProvider>
      <AlertsProvider>
        {location.pathname === '/admin' && !isAdmin ? (
          <Layout>
            <div className="flex items-center justify-center min-h-screen">
              <div className="text-center">
                <h1 className="text-2xl font-bold text-gray-900 mb-4">Admin Access Required</h1>
                <p className="text-gray-600">You don't have permission to access the admin area.</p>
              </div>
            </div>
          </Layout>
        ) : (
          <Layout>
            <Suspense fallback={<PageLoader />}>
              <Routes>
                <Route path="/dashboard" element={<Dashboard />} />
                <Route path="/property/:propertyId" element={<PropertyDetail />} />
                <Route path="/settings" element={<SettingsPage />} />
                <Route path="/admin" element={<AdminPage />} />
                <Route path="/ledger" element={<LedgerDemo />} />
                <Route path="/ranking" element={<Ranking />} />
                <Route path="/leaderboard" element={<Navigate to="/ranking" replace />} />
                <Route path="/portfolio-analysis" element={<PortfolioAnalysis />} />
                <Route path="/market-data" element={<MarketData />} />
                <Route path="/scenarios" element={<ScenarioPlanner />} />
                <Route path="/retirement" element={<RetirementProjectionPage />} />
                <Route path="/tools" element={<InvestmentTools />} />
                <Route path="/investment-tools" element={<InvestmentTools />} />
                <Route path="/stocks" element={<StockPortfolioPage />} />

                <Route path="/investment-planner" element={<InvestmentPlannerPage />} />
                <Route path="/soa-report" element={<SOAReportPage />} />
                <Route path="/blog" element={<BlogListPage />} />
                <Route path="/blog/:slug" element={<BlogPostPage />} />
                <Route path="/pages/:slug" element={<ContentPageView />} />
                <Route path="/*" element={<PropertyInspectionPage />} />
              </Routes>
            </Suspense>
          </Layout>
        )}
      </AlertsProvider>
    </PortfolioProvider>
  );
}

import { usePageTracking } from '../hooks/usePageTracking';

export function App() {
  return (
    <>
      <ToastProvider>
        <AuthProvider>
          <CountryProvider>
            <ThemeProvider>
              <Router
                future={{
                  v7_startTransition: true,
                  v7_relativeSplatPath: true,
                }}
              >
                <PageTracker />
                <GlobalMonitoring />
                <ScrollToTop />
                <ThemeManager />
                <Suspense fallback={<PageLoader />}>
                  <Routes>
                    <Route path="/" element={<PublicHome />} />
                    <Route path="/login" element={<LoginForm />} />
                    <Route path="/register" element={<RegisterPage />} />
                    <Route path="/forgot-password" element={<ForgotPassword />} />
                    <Route path="/pricing" element={<PricingPage />} />
                    <Route path="/trial-expired" element={<ExpiredTrialPage />} />
                    {/* Public routes that adapt based on auth state */}
                    <Route path="/blog" element={<BlogRoute />} />
                    <Route path="/blog/:slug" element={<BlogRoute isPost />} />
                    <Route path="/calculators" element={<PublicPageWrapper><PropertyCalculatorHub /></PublicPageWrapper>} />
                    <Route path="/calculators/rental-yield" element={<PublicPageWrapper><YieldCalculatorPage /></PublicPageWrapper>} />
                    <Route path="/calculators/upfront-costs" element={<PublicPageWrapper><UpfrontCostsCalculator /></PublicPageWrapper>} />
                    <Route path="/calculators/borrowing-power" element={<PublicPageWrapper><BorrowingPowerCalculator /></PublicPageWrapper>} />
                    <Route path="/calculators/rate-sensitivity" element={<PublicPageWrapper><RateSensitivityCalculator /></PublicPageWrapper>} />
                    <Route path="/calculators/extra-repayments" element={<PublicPageWrapper><ExtraRepaymentsCalculator /></PublicPageWrapper>} />
                    <Route path="/calculators/fhog" element={<PublicPageWrapper><FHOGCalculator /></PublicPageWrapper>} />
                    <Route path="/calculators/growth-equity" element={<PublicPageWrapper><GrowthEquityCalculator /></PublicPageWrapper>} />
                    <Route path="/pages/about-us" element={<Suspense fallback={<PageLoader />}><AboutUs /></Suspense>} />
                    <Route path="/pages/contact-us" element={<Suspense fallback={<PageLoader />}><ContactUs /></Suspense>} />
                    <Route path="/pages/technical-documentation" element={<PublicPageWrapper><Suspense fallback={<PageLoader />}><ProductTechnicalOverview /></Suspense></PublicPageWrapper>} />
                    <Route path="/pages/:slug" element={<PublicPageWrapper><ContentPageView /></PublicPageWrapper>} />
                    <Route path="/debug-management-fees" element={<Suspense fallback={<PageLoader />}><DebugManagementFees /></Suspense>} />
                    <Route path="/*" element={<AuthenticatedApp />} />
                  </Routes>
                </Suspense>
              </Router>
            </ThemeProvider>
          </CountryProvider>
        </AuthProvider>
      </ToastProvider>
    </>
  );
}

function PageTracker() {
  usePageTracking();
  return null;
}

/**
 * Global Monitoring Component
 * Handles application-level data capture that isn't page-specific
 */
function GlobalMonitoring() {
  const { user } = useAuth();

  useEffect(() => {
    // 1. Capture device and browser info on mount
    analytics.captureDeviceInfo();

    // 2. Setup global error listeners
    const handleError = (event: ErrorEvent) => {
      analytics.trackError(
        'runtime_error',
        event.message,
        event.error?.stack,
        true // Treat most runtime errors as potentially fatal
      );
    };

    const handleRejection = (event: PromiseRejectionEvent) => {
      analytics.trackError(
        'unhandled_rejection',
        event.reason?.message || 'Unknown promise rejection',
        event.reason?.stack,
        false
      );
    };

    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleRejection);

    return () => {
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleRejection);
    };
  }, []);

  return null;
}

export default App;